/**
 * @file CountersServicesGroup.tsx
 * @description Группа «Счётчики» панели «Счётчики и cookie» (phase19.16,
 * партия Р3г, v0.19.62; SB48, SB133; uiux-r3 §3.6 с исправлением §7.29 п. 4:
 * номер VK — числовой ID пикселя VK Рекламы):
 *  - Яндекс Метрика — номер; Вебвизор (по умолчанию выключен, доступен при
 *    включённой Метрике; включение спросит согласие заново, SB135);
 *  - VK Реклама — номер пикселя;
 *  - Google Analytics не подключаем (SB48).
 * Поле пустое — сервис выключен.
 *
 * «SEO-техника» v0.19.65 (SB147; uiux-seo-tech §3.5; требование
 * пользователя 2026-10-25): под Вебвизором — «Передавать данные о товарах и
 * заказах (электронная коммерция)», по умолчанию включено, без номера
 * Метрики недоступно; подсказка, что включить в самой Метрике; пока галочка
 * включена — напоминание об абзаце политики ПДн; под номером VK — что
 * получит VK и какое событие добавить в VK Рекламе. Смена номера и
 * Вебвизора раскрывают прежнюю Метрику (`metrikaWith`) — галочка не теряется.
 */

import { metrikaEcommerceEnabled } from "@chaiberi/shared";
import { Link } from "react-router";

import { NoticeStrip } from "../../ui/NoticeStrip";
import { SettingsGroup, FieldHint } from "../../ui/settings-groups";
import { CounterIdField } from "./CounterIdField";
import { useCounters } from "./counters-context";
import { SwitchRow } from "./SwitchRow";
import { useCurrentContext } from "../../../auth/hooks";
import {
  ECOMMERCE_HINT,
  ECOMMERCE_LABEL,
  ECOMMERCE_METRIKA_STEPS,
  ECOMMERCE_NO_METRIKA,
  ECOMMERCE_POLICY_NOTE,
  NO_GA_TEXT,
  VK_PURCHASE_HINT,
  WEBVISOR_HINT,
  metrikaWith,
  servicesSummary,
} from "../../../lib/site-view/counters-form";

export function CountersServicesGroup() {
  const { counters, update } = useCounters();
  const slug = useCurrentContext()?.shop?.slug ?? "";
  const metrika = counters.metrika;
  const ecommerce = metrikaEcommerceEnabled(counters);
  return (
    <SettingsGroup id="services" title="Счётчики" hint="Номер счётчика — код на сайт ставит платформа." summary={servicesSummary(counters)}>
      <div className="flex min-w-0 flex-col gap-4">
        <CounterIdField service="metrika" value={metrika?.id ?? null} onValid={(id) => update({ metrika: id === null ? null : metrikaWith(metrika, { id }) })} />
        <SwitchRow
          label="Вебвизор"
          hint={metrika ? WEBVISOR_HINT : ECOMMERCE_NO_METRIKA}
          checked={metrika?.webvisor ?? false}
          disabled={metrika === null}
          onChange={(webvisor) => (metrika ? update({ metrika: metrikaWith(metrika, { webvisor }) }) : undefined)}
        />
        <SwitchRow
          label={ECOMMERCE_LABEL}
          hint={
            metrika ? (
              <>
                <span className="block">{ECOMMERCE_HINT}</span>
                <span className="mt-1 block">{ECOMMERCE_METRIKA_STEPS}</span>
              </>
            ) : (
              ECOMMERCE_NO_METRIKA
            )
          }
          checked={ecommerce}
          disabled={metrika === null}
          onChange={(on) => (metrika ? update({ metrika: metrikaWith(metrika, { ecommerce: on }) }) : undefined)}
        />
        {metrika && ecommerce ? (
          <NoticeStrip tone="warning">
            {ECOMMERCE_POLICY_NOTE}{" "}
            <Link to={`/shop/${slug}/settings/legal-documents`} className="inline-flex min-h-12 items-center font-medium text-primary hover:underline lg:min-h-0">
              Открыть документы →
            </Link>
          </NoticeStrip>
        ) : null}
        <div className="flex min-w-0 flex-col">
          <CounterIdField service="vk" value={counters.vk?.id ?? null} onValid={(id) => update({ vk: id === null ? null : { id } })} />
          {counters.vk ? <FieldHint>{VK_PURCHASE_HINT}</FieldHint> : null}
        </div>
        <FieldHint className="mt-0">{NO_GA_TEXT}</FieldHint>
      </div>
    </SettingsGroup>
  );
}
